import { useEffect, useRef } from "react";
import { useRouter } from "next/router";
import useLayoutStore from "@/stores/useLayoutStore";
import { afterPaint } from "@/lib/utils";

/**
 * Closes menus on route changes: both the desktop dropdown and the mobile menu
 * close on routeChangeComplete, so the fade reveals the new page.
 */
export function useRouteTransition() {
  const router = useRouter();
  const isMenuOpen = useLayoutStore((state) => state.isMenuOpen);
  const closeMenu = useLayoutStore((state) => state.closeMenu);
  const activeDropdown = useLayoutStore((state) => state.activeDropdown);
  const setActiveDropdown = useLayoutStore((state) => state.setActiveDropdown);

  const prevAsPathRef = useRef(router.asPath);

  // Helper to check if this is a hash-only change (same page, different anchor)
  const isHashOnlyChange = (prevPath: string, newPath: string): boolean => {
    const [prevPathname] = prevPath.split("#");
    const [newPathname] = newPath.split("#");
    return prevPathname === newPathname && prevPath !== newPath;
  };

  // Close menus on hash-only changes (e.g., /product → /product#faq)
  // Full route changes are handled by routeChangeComplete event
  useEffect(() => {
    const prevPath = prevAsPathRef.current;
    prevAsPathRef.current = router.asPath;

    // Skip if path hasn't changed or if this is a full route change
    if (
      prevPath === router.asPath ||
      !isHashOnlyChange(prevPath, router.asPath)
    ) {
      return;
    }

    // Hash-only change - close menus immediately (no page transition)
    if (activeDropdown) {
      setActiveDropdown(null);
    }
    if (isMenuOpen) {
      closeMenu();
    }
  }, [router.asPath, activeDropdown, setActiveDropdown, isMenuOpen, closeMenu]);

  useEffect(() => {
    const handleRouteChangeComplete = () => {
      // Read current state at execution time to avoid stale closures
      const { isMenuOpen, closeMenu, activeDropdown, setActiveDropdown } =
        useLayoutStore.getState();

      // Close desktop dropdown after navigation completes
      if (activeDropdown) {
        setActiveDropdown(null);
      }

      if (isMenuOpen) {
        if (useLayoutStore.getState().pendingAccessoriesReopen) {
          useLayoutStore.setState({ pendingAccessoriesReopen: false });
        } else {
          afterPaint(closeMenu);
        }
      }
    };

    const handleRouteChangeError = () => {
      const { isMenuOpen, closeMenu, activeDropdown, setActiveDropdown } =
        useLayoutStore.getState();

      if (isMenuOpen) {
        closeMenu();
      }
      if (activeDropdown) {
        setActiveDropdown(null);
      }
    };

    router.events.on("routeChangeComplete", handleRouteChangeComplete);
    router.events.on("routeChangeError", handleRouteChangeError);

    return () => {
      router.events.off("routeChangeComplete", handleRouteChangeComplete);
      router.events.off("routeChangeError", handleRouteChangeError);
    };
  }, [router.events]);
}
